import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { OnboardingSources } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { SAVE_AS_DRAFT_CONTACT_FORM_KEY } from 'legacy/lut/components/widgets/public/contactEditDialog/configs'
import { getInitialSaveAsDraftContact } from 'legacy/lut/components/widgets/public/contactEditDialog/helpers/getInitialSaveAsDraftContact'
import { TDraft } from 'legacy/lut/components/widgets/public/contactEditDialog/models'
import { useGetPartnerData } from 'legacy/lut/features/partner/hooks'
import { checkEmailDomain } from 'legacy/lut/helpers/validateRules/checkEmailDomain'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useValidationRulesAndEmailList } from 'legacy/lut/hooks/useValidationRulesAndEmailList'
import { selectAssociativeLanguages, selectLanguages } from 'legacy/lut/selectors/app'
import { selectCountries } from 'legacy/lut/selectors/app'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { Static } from 'core-components/types'
import { guid } from 'core/classes/helpers/GuidHelper'
import { IReference } from 'core/classes/models/IReference'
import { Crud } from 'core/types/Crud'
import { showError } from 'onboarding/classes/services/notification'
import { refreshProfilingProgressData } from 'onboarding/features/actions/refreshProgressData'
import { contactService } from 'onboarding/services/contactService'
import { EmailHelper } from 'onboarding/utils/email'

import { FormFields } from './FormFields'

import styles from './SaveAsDraftContact.module.scss'

export interface IDraftContactData {
  PartnerId: string
  CountryOfBusiness?: string
  Market?: string

  FirstName?: string
  LastName?: string
  Email?: string
  PreferredLanguage?: string
  ContactID?: string
  OnboardingRequestId?: number
}

interface IProps {
  draftContact: IDraftContactData | DataModel
  onClose: (showWarning: boolean) => void
  appSource?: string
  onApply?: (contact: DataModel) => void
}

export const SaveAsDraftContact = ({
  draftContact: draftContactProp,
  onClose,
  onApply,
  appSource,
}: IProps) => {
  // ----------------------------- INIT ---------------------------------
  const dispatch = useAppDispatch()

  const draftContact = useMemo(
    () =>
      draftContactProp instanceof DataModel
        ? draftContactProp.clone()
        : new DataModel(draftContactProp),
    [draftContactProp]
  )

  const [isSaving, setIsSaving] = useState(false)
  const [writeDraft, setWriteDraft] = useState<TDraft>(() => ({
    ...getInitialSaveAsDraftContact(),
    country: draftContact.get('CountryOfBusiness'),
  }))

  // ----------------------------- STORE ---------------------------------
  const associativeLanguages = useSelector(selectAssociativeLanguages)
  const languages = useSelector(selectLanguages)
  const countries = useSelector(selectCountries)

  const [{ formData, modifiedFields }] = useGetFormData<TDraft>(SAVE_AS_DRAFT_CONTACT_FORM_KEY)

  const trimmedEmail = formData.email?.trim() ?? ''
  const partnerId = draftContact.get('PartnerID')
  const market = draftContact.get('Market')

  // ----------------------------- DATA ---------------------------------
  const preferredLanguages = useMemo<IReference[]>(() => {
    if (!formData.country) return []

    return associativeLanguages
      .filter((lang) => lang.CountryCode === formData.country)
      .map((lang) => {
        const pretty = languages.find((l) => l.value === lang.value)
        return {
          name: pretty?.name ?? lang.name,
          value: pretty?.value ?? lang.value,
        }
      })
  }, [associativeLanguages, languages, formData.country])

  const dropdownSources = useMemo(
    () => ({
      countries: new Crud({
        fetcher: new Static(countries.filter((c) => c.market === market)),
        keyField: 'value',
        defaultPaging: { page: 0, pageSize: 1000 },
      }),
      preferredLanguages: new Crud({
        fetcher: new Static(preferredLanguages),
        keyField: 'name',
      }),
    }),
    [countries, market, preferredLanguages]
  )

  const { validationRules, emailList } = useValidationRulesAndEmailList(
    partnerId,
    draftContact.get('CountryOfBusiness')
  )

  const { partnerData } = useGetPartnerData(partnerId)

  // ----------------------------- HELPERS ---------------------------------
  const applyDraftFormToContact = (model: DataModel, form: TDraft, email: string) => {
    model.set('FirstName', form.firstName)
    model.set('LastName', form.lastName)
    model.set('CountryOfBusiness', form.country)
    model.set('Email', email)
    model.set('PreferredLanguage', form.preferredLanguage)
    model.set('ContactID', `new-${guid()}`)
  }

  const isValidEmailDomain = async (email: string) => {
    if (EmailHelper.isHPDomain(email)) {
      return 'HP email cannot be used'
    }

    return checkEmailDomain({
      value: email,
      companyUrl: partnerData?.url ?? '',
      validationRules,
      emailList,
    })
  }

  const checkCanSave = async (email: string) => {
    const errors = await validateFormData(formData, SAVE_AS_DRAFT_CONTACT_FORM_KEY)

    if (errors.length) {
      return { isValid: false, isContactExist: false }
    }

    const isContactExist = await contactService.checkExistenceByEmail(partnerId, email)

    return { isValid: true, isContactExist }
  }

  // ----------------------------- EFFECTS ---------------------------------
  useEffect(() => {
    if (preferredLanguages.length === 1) {
      setWriteDraft((prev) => ({
        ...prev,
        preferredLanguage: preferredLanguages[0].name ?? '',
      }))
    }
  }, [preferredLanguages])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(SAVE_AS_DRAFT_CONTACT_FORM_KEY))
    }
  }, [dispatch])

  // ----------------------------- ACTIONS ---------------------------------
  const onSave = async () => {
    setIsSaving(true)

    try {
      const { isValid, isContactExist } = await checkCanSave(trimmedEmail)

      if (!isValid) return

      if (isContactExist) {
        showError(`Contact with email '${trimmedEmail}' already exists!`)
        return
      }

      const localDraft = draftContact.clone()
      applyDraftFormToContact(localDraft, formData, trimmedEmail)

      const onboardingRequestId = getUrlParam('rid')
      if (onboardingRequestId) {
        localDraft.set('OnboardingRequestId', Number(onboardingRequestId))
      }

      onApply?.(localDraft)

      onClose(appSource !== OnboardingSources.BPRegistration)
      dispatch(refreshProfilingProgressData())
    } catch (e: any) {
      showError(e.message)
    } finally {
      setIsSaving(false)
    }
  }

  // ----------------------------- RENDER ---------------------------------
  return (
    <div className={styles.root}>
      <h4>Save as draft</h4>

      <div className={styles.formFieldsWrapper}>
        <FormFields
          {...writeDraft}
          market={market}
          dropdownSources={dropdownSources}
          isValidEmailDomain={isValidEmailDomain}
        />
      </div>

      <Tooltip
        className={styles.saveButtonTooltip}
        tipContent={() => <span>No changes to save</span>}
        disabled={!!modifiedFields.length}
      >
        <Button disabled={!modifiedFields.length} loading={isSaving} onClick={onSave}>
          Save
        </Button>
      </Tooltip>
    </div>
  )
}
